import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  emailVerified: boolean;
  gaId: string | null;
  termsOfService?: Record<string, string>;
  /** Platform role names, e.g. ['admin']. Present on /me + login responses. */
  roles?: string[];
}

const meKey = ['me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: () => api.get<AuthUser>('/api/Clients/me'),
    retry: false,
    staleTime: 60_000,
  });
}

export type MfaChallenge = { mfaRequired: true; mfaToken: string };
export type LoginResponse = AuthUser | MfaChallenge;

export function isMfaChallenge(r: LoginResponse): r is MfaChallenge {
  return (r as MfaChallenge).mfaRequired === true;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<LoginResponse>('/api/Clients/login', input),
    onSuccess: (response) => {
      // Only seed the /me cache when we actually got an authenticated user.
      // The MFA challenge response is not a user.
      if (!isMfaChallenge(response)) {
        qc.setQueryData(meKey, response);
      }
    },
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mfaToken: string; code: string }) =>
      api.post<AuthUser>('/api/Clients/mfa/verify', input),
    onSuccess: (user) => {
      qc.setQueryData(meKey, user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/Clients/logout'),
    onSuccess: () => {
      qc.setQueryData(meKey, null);
      qc.clear();
    },
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; email: string; password: string }) =>
      api.post<AuthUser>('/api/Clients/register', input),
    onSuccess: (user) => {
      qc.setQueryData(meKey, user);
    },
  });
}
